// Timezone utility functions for Indian Standard Time (IST)
// This ensures consistent timezone across all dashboards

const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Get current date and time in IST
 * @returns {Date} Current date in IST
 */
export const getCurrentISTDate = () => {
  return new Date(new Date().toLocaleString("en-US", { timeZone: IST_TIMEZONE }));
};

/**
 * Format date to IST string
 * @param {Date|string} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string in IST
 */
export const formatDateIST = (date = new Date(), options = {}) => {
  const defaultOptions = {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Format date and time to IST string
 * @param {Date|string} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date and time string in IST
 */
export const formatDateTimeIST = (date = new Date(), options = {}) => {
  const defaultOptions = {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleString('en-US', defaultOptions);
};

/**
 * Get current date in YYYY-MM-DD format (IST)
 * @returns {string} Date in YYYY-MM-DD format
 */
export const getCurrentDateIST = () => {
  const now = getCurrentISTDate();
  return now.toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE }); // en-CA gives YYYY-MM-DD format
};

/**
 * Get current time in HH:MM format (IST)
 * @returns {string} Time in HH:MM format
 */
export const getCurrentTimeIST = () => {
  const now = getCurrentISTDate();
  return now.toLocaleTimeString('en-US', { 
    timeZone: IST_TIMEZONE,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format date for display with weekday (IST)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date with weekday
 */
export const formatDateWithWeekdayIST = (date = new Date()) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    timeZone: IST_TIMEZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Convert any date to IST and format for input fields
 * @param {Date|string} date - Date to convert
 * @returns {string} Date in YYYY-MM-DD format (IST)
 */
export const convertToISTInputFormat = (date) => {
  if (!date) return getCurrentDateIST();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE });
};

/**
 * Get IST timezone info
 * @returns {Object} Timezone information
 */
export const getISTInfo = () => {
  const now = new Date();
  const istTime = new Date(now.toLocaleString("en-US", { timeZone: IST_TIMEZONE }));
  const utcTime = new Date(now.toUTCString());
  const offsetMinutes = (istTime.getTime() - utcTime.getTime()) / (1000 * 60);
  
  return {
    timezone: IST_TIMEZONE,
    offsetMinutes,
    offsetHours: offsetMinutes / 60,
    currentTime: istTime,
    displayName: 'India Standard Time (IST)'
  };
};

/**
 * Format date for database storage (ISO string but ensure IST context)
 * @param {Date|string} date - Date to format
 * @returns {string} ISO string
 */
export const formatForDatabase = (date = new Date()) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString();
};

/**
 * Parse database date and display in IST
 * @param {string} isoString - ISO date string from database
 * @returns {string} Formatted date in IST
 */
export const parseFromDatabase = (isoString, options = {}) => {
  if (!isoString) return 'N/A';
  return formatDateIST(new Date(isoString), options);
};

/**
 * Get relative time in IST (e.g., "2 hours ago")
 * @param {Date|string} date - Date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTimeIST = (date) => {
  const now = getCurrentISTDate();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return formatDateIST(dateObj, { month: 'short', day: 'numeric' });
};

// Export default object with all functions
export default {
  getCurrentISTDate,
  formatDateIST,
  formatDateTimeIST,
  getCurrentDateIST,
  getCurrentTimeIST,
  formatDateWithWeekdayIST,
  convertToISTInputFormat,
  getISTInfo,
  formatForDatabase,
  parseFromDatabase,
  getRelativeTimeIST,
  IST_TIMEZONE
};